import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";

import HomePage from "@/pages/HomePage";
import EnginesPage from "@/pages/EnginesPage";
import DemoPage from "@/pages/DemoPage";
import CaseStudyPage from "@/pages/CaseStudyPage";
import ProgramPage from "@/pages/ProgramPage";
import PricingPage from "@/pages/PricingPage";
import FounderPage from "@/pages/FounderPage";

import LoginPage from "@/pages/admin/LoginPage";
import ForgotPasswordPage from "@/pages/admin/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/admin/ResetPasswordPage";
import AuthCallback from "@/pages/admin/AuthCallback";
import PinSettingsPage from "@/pages/admin/PinSettingsPage";
import BillingPage from "@/pages/admin/BillingPage";
import UsersPage from "@/pages/admin/UsersPage";
import SmsConsolePage from "@/pages/admin/SmsConsolePage";
import FinanceOverview from "@/pages/admin/finance/OverviewPage";
import FinanceTransactions from "@/pages/admin/finance/TransactionsPage";
import FinanceExpenses from "@/pages/admin/finance/ExpensesPage";
import FinanceBills from "@/pages/admin/finance/BillsPage";
import FinanceDistributions from "@/pages/admin/finance/DistributionsPage";
import FinanceForecast from "@/pages/admin/finance/ForecastPage";
import FinanceReports from "@/pages/admin/finance/ReportsPage";
import ContactsListPage from "@/pages/admin/crm/ContactsListPage";
import ContactDetailPage from "@/pages/admin/crm/ContactDetailPage";
import DocumentCenterPage from "@/pages/admin/documents/DocumentCenterPage";
import SandboxPage from "@/pages/sandbox/SandboxPage";
import TutorialManager from "@/pages/admin/TutorialManager";
import MicroLandingPage from "@/pages/public/MicroLandingPage";
import ResponsePage from "@/pages/public/ResponsePage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import BriefDetail from "@/pages/admin/BriefDetail";
import PreviewManager from "@/pages/admin/PreviewManager";
import LayoutEditorPage from "@/pages/admin/LayoutEditorPage";
import LabelsEditorPage from "@/pages/admin/LabelsEditorPage";
import NdsSchemaEditorPage from "@/pages/admin/NdsSchemaEditorPage";
import DatabaseManagerPage from "@/pages/admin/DatabaseManagerPage";
import RoiDashboardPage from "@/pages/admin/RoiDashboardPage";
import OsPage from "@/pages/OsPage";
import NewBriefPage from "@/pages/NewBriefPage";
import PreviewPublic from "@/pages/PreviewPublic";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutCancel from "@/pages/CheckoutCancel";
import PrivacyPage from "@/pages/PrivacyPage";
import DemoDirectoryPage from "@/pages/DemoDirectoryPage";
import DemoUnlockPage from "@/pages/DemoUnlockPage";
import ClientDemoPage from "@/pages/ClientDemoPage";
import DemoDirectoryManagerPage from "@/pages/admin/DemoDirectoryManagerPage";
import PilotRequestsPage from "@/pages/admin/PilotRequestsPage";
import { FormsPage, RecordsPage } from "@/pages/admin/FormsPage";
import ShareLinkPage from "@/pages/ShareLinkPage";
import DemoPreviewPage from "@/pages/DemoPreviewPage";

import { AuthProvider, ProtectedRoute } from "@/lib/auth";
import { DynamicEngineProvider } from "@/lib/dynamicEngine";
import "@/lib/labelKeys";   // seeds label registry at boot

// Public pages get header + footer; admin/preview/checkout pages don't (they have their own chrome)
const PublicShell = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
  </>
);

const AppRoutes = () => {
  const { pathname } = useLocation();
  const bareRoute =
    pathname.startsWith("/admin") ||
    pathname.startsWith("/preview/") ||
    pathname.startsWith("/checkout") ||
    pathname.startsWith("/c/") ||
    pathname.startsWith("/r/") ||
    pathname.startsWith("/sandbox") ||
    pathname.startsWith("/os") ||
    pathname.startsWith("/demo") ||
    pathname.startsWith("/demos") ||
    pathname.startsWith("/d/") ||
    pathname.startsWith("/demo/") ||
    pathname.startsWith("/new-brief") ||
    pathname === "/privacy";

  return (
    <div className="App min-h-screen">
      {bareRoute ? (
        <Routes>
          {/* Admin */}
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
          <Route path="/admin/auth/callback" element={<AuthCallback />} />
          <Route path="/admin/settings" element={<ProtectedRoute><PinSettingsPage /></ProtectedRoute>} />
          <Route path="/admin/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
          <Route path="/admin/sms" element={<ProtectedRoute><SmsConsolePage /></ProtectedRoute>} />
          <Route path="/admin/finance" element={<ProtectedRoute><FinanceOverview /></ProtectedRoute>} />
          <Route path="/admin/finance/transactions" element={<ProtectedRoute><FinanceTransactions /></ProtectedRoute>} />
          <Route path="/admin/finance/expenses" element={<ProtectedRoute><FinanceExpenses /></ProtectedRoute>} />
          <Route path="/admin/finance/bills" element={<ProtectedRoute><FinanceBills /></ProtectedRoute>} />
          <Route path="/admin/finance/distributions" element={<ProtectedRoute><FinanceDistributions /></ProtectedRoute>} />
          <Route path="/admin/finance/forecast" element={<ProtectedRoute><FinanceForecast /></ProtectedRoute>} />
          <Route path="/admin/finance/reports" element={<ProtectedRoute><FinanceReports /></ProtectedRoute>} />
          <Route path="/admin/contacts" element={<ProtectedRoute><ContactsListPage /></ProtectedRoute>} />
          <Route path="/admin/contacts/:id" element={<ProtectedRoute><ContactDetailPage /></ProtectedRoute>} />
          <Route path="/admin/documents" element={<ProtectedRoute><DocumentCenterPage /></ProtectedRoute>} />
          <Route path="/admin/sandbox" element={<ProtectedRoute><SandboxPage isPresenter /></ProtectedRoute>} />
          <Route path="/admin/tutorials" element={<ProtectedRoute><TutorialManager /></ProtectedRoute>} />
          <Route path="/sandbox" element={<SandboxPage />} />
          <Route path="/sandbox/:sessionId" element={<SandboxPage />} />
          <Route path="/c/:token" element={<MicroLandingPage />} />
          <Route path="/r/:token" element={<ResponsePage />} />
          <Route path="/admin" element={<ProtectedRoute><OsPage /></ProtectedRoute>} />
          <Route path="/admin/briefs" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/brief/:id" element={<ProtectedRoute><BriefDetail /></ProtectedRoute>} />
          <Route path="/admin/previews" element={<ProtectedRoute><PreviewManager /></ProtectedRoute>} />
          <Route path="/admin/layout" element={<ProtectedRoute><LayoutEditorPage /></ProtectedRoute>} />
          <Route path="/admin/labels" element={<ProtectedRoute><LabelsEditorPage /></ProtectedRoute>} />
          <Route path="/admin/nds" element={<ProtectedRoute><NdsSchemaEditorPage /></ProtectedRoute>} />
          <Route path="/admin/roi" element={<ProtectedRoute><RoiDashboardPage /></ProtectedRoute>} />
          <Route path="/admin/db" element={<ProtectedRoute><DatabaseManagerPage /></ProtectedRoute>} />
          <Route path="/admin/demos" element={<ProtectedRoute><DemoDirectoryManagerPage /></ProtectedRoute>} />
          <Route path="/admin/pilot-requests" element={<ProtectedRoute><PilotRequestsPage /></ProtectedRoute>} />
          <Route path="/admin/forms" element={<ProtectedRoute><FormsPage /></ProtectedRoute>} />
          <Route path="/admin/records/:formId" element={<ProtectedRoute><RecordsPage /></ProtectedRoute>} />
          {/* NexEngines OS — universal core layout */}
          <Route path="/os" element={<ProtectedRoute><OsPage /></ProtectedRoute>} />
          {/* Public NDS-1 prospect demo — same layout as /os, sandboxed writes */}
          <Route path="/demo" element={<DemoPage />} />
          {/* Client Demo Directory — public list → unlock → client page */}
          <Route path="/demos" element={<DemoDirectoryPage />} />
          <Route path="/demos/:slug/unlock" element={<DemoUnlockPage />} />
          <Route path="/demos/:slug" element={<ClientDemoPage />} />
          <Route path="/d/:token" element={<ShareLinkPage />} />
          <Route path="/demo/:slug/preview" element={<DemoPreviewPage />} />
          {/* Client preview */}
          <Route path="/new-brief" element={<NewBriefPage />} />
          <Route path="/preview/:slug" element={<PreviewPublic />} />
          {/* Checkout */}
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />
          <Route path="/privacy" element={<PrivacyPage />} />
        </Routes>
      ) : (
        <PublicShell>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/engines" element={<EnginesPage />} />
            <Route path="/case-study" element={<CaseStudyPage />} />
            <Route path="/program" element={<ProgramPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/founder" element={<FounderPage />} />
          </Routes>
        </PublicShell>
      )}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DynamicEngineProvider>
          <ScrollToTop />
          <AppRoutes />
          <Toaster
            position="top-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "#0F172A",
                border: "1px solid #334155",
                color: "#E2E8F0",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "12px",
                borderRadius: "0",
              },
            }}
          />
        </DynamicEngineProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
